/**
 * RuneCatcher minigame — a rune falls from top to bottom of a column.
 * Player taps/presses Space when it's in the target zone.
 * Score = timing precision.
 */

import type { Minigame, MinigameState } from "./manager";

/** Rune glyphs drawn from the arcane alphabet. */
const RUNES = ["\u16A0", "\u16A2", "\u16A6", "\u16B1", "\u16B7", "\u16C1", "\u16C7", "\u16D2"];

/** Time limit in milliseconds. */
const TIME_LIMIT = 5000;

/** Fall speed: fraction of height per millisecond. */
const FALL_SPEED = 1.0 / TIME_LIMIT;

/** Center of the target zone (70% of height). */
const TARGET_CENTER = 0.7;

/** Half-width of the tolerance band (15%). */
const TARGET_TOLERANCE = 0.15;

export class RuneCatcher implements Minigame {
  readonly id = "rune-catcher";
  readonly name = "Rune Catcher";

  init(): MinigameState {
    const rune = RUNES[Math.floor(Math.random() * RUNES.length)];
    return {
      elapsed: 0,
      position: 0,
      caught: false,
      score: 0,
      rune,
    };
  }

  tick(dt: number, state: MinigameState): MinigameState {
    if (state.caught) return state;
    const elapsed = (state.elapsed as number) + dt;
    const position = Math.min((state.position as number) + FALL_SPEED * dt, 1.0);
    return { ...state, elapsed, position };
  }

  onInput(state: MinigameState): MinigameState {
    // Only count the first input
    if (state.caught) return state;

    const pos = state.position as number;
    const dist = Math.abs(pos - TARGET_CENTER);

    let score: number;
    if (dist > TARGET_TOLERANCE) {
      score = 0;
    } else {
      // Linear interpolation: 0 distance = 100, tolerance distance = 25
      const ratio = 1 - dist / TARGET_TOLERANCE;
      score = Math.round(25 + 75 * ratio);
    }

    return { ...state, caught: true, score };
  }

  isComplete(state: MinigameState): boolean {
    return (state.caught as boolean) || (state.elapsed as number) >= TIME_LIMIT;
  }

  score(state: MinigameState): number {
    return state.score as number;
  }

  render(width: number, height: number, state: MinigameState): string[] {
    const lines: string[] = [];
    const pos = state.position as number;
    const rune = state.rune as string;
    const runeRow = Math.floor(pos * (height - 1));

    // Target zone rows
    const targetTop = Math.floor((TARGET_CENTER - TARGET_TOLERANCE) * (height - 1));
    const targetBot = Math.floor((TARGET_CENTER + TARGET_TOLERANCE) * (height - 1));

    const colCenter = Math.floor(width / 2);

    for (let r = 0; r < height; r++) {
      const chars = Array.from({ length: width }, () => " ");

      // Column borders
      if (colCenter - 2 >= 0) chars[colCenter - 2] = "|";
      if (colCenter + 2 < width) chars[colCenter + 2] = "|";

      // Target zone markers
      if (r >= targetTop && r <= targetBot) {
        if (colCenter - 1 >= 0) chars[colCenter - 1] = "=";
        if (colCenter + 1 < width) chars[colCenter + 1] = "=";
      }

      // Rune glyph
      if (r === runeRow) {
        chars[colCenter] = rune;
      }

      lines.push(chars.join(""));
    }

    return lines;
  }
}
